import { StudentAssignment } from './../../../../core/interfaces/student';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';

import { ProfileCardComponent } from '../../../ui/views/profile-card/profile-card.component';
import { TodayClassesComponent } from '../../../ui/views/today-classes/today-classes.component';
import { PieLegendComponent } from '../../../../shared/charts/pie-legend/pie-legend.component';

import { PrimHeroComponent } from '../../../ui/views/dashboard/prim-hero/prim-hero.component';
import { PrimQuestCardComponent } from '../../../ui/views/dashboard/prim-quest-card/prim-quest-card.component';
import { PrimXpCardComponent, PrimXpData } from '../../../ui/views/dashboard/prim-xp-card/prim-xp-card.component';
import { PrimBadgesCardComponent, PrimBadge } from '../../../ui/views/dashboard/prim-badges-card/prim-badges-card.component';
import { SubjectsCardComponent, DashboardSubject } from '../../../ui/views/dashboard/subjects-card/subjects-card.component';
import { RecentActivityCardComponent, RecentResult } from '../../../ui/views/dashboard/recent-activity-card/recent-activity-card.component';
import { StatsBreakdownCardComponent } from '../../../ui/views/dashboard/stats-breakdown-card/stats-breakdown-card.component';
import { LeaderboardCardComponent } from '../../../ui/views/dashboard/leaderboard-card/leaderboard-card.component';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import { EmptyStateComponent } from '../../../ui/empty-state/empty-state.component';

import { AuthService } from '../../../../core/services/utils/auth.service';
import { StudentTestService } from '../../../../core/services/student-test.service';
import { StudentService } from '../../../../core/services/student.service';
import { GamificationService } from '../../../../core/services/gamification.service';
import { AgeTierService } from '../../../../core/services/utils/age-tier.service';
import { StudentSubjectProgression } from '../../../../core/interfaces/student-subject-progression';
import { LeaderboardEntry, StudentXpSummary } from '../../../../core/interfaces/gamification';
import { StudentCalendarService } from '../../../../core/services/academic-appointments/student-calendar.service';
import { StudentFeatureService } from '../../../../core/services/student-feature.service';
import { LocalizedLabelService } from '../../../../core/services/utils/localized-label.service';
import {
  AcademicAppointment,
  sessionStart,
} from '../../../../core/interfaces/academic-appointment';

@Component({
  selector: 'app-student-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    RouterModule,
    ProfileCardComponent,
    TodayClassesComponent,
    PieLegendComponent,
    PrimHeroComponent,
    PrimQuestCardComponent,
    PrimXpCardComponent,
    PrimBadgesCardComponent,
    SubjectsCardComponent,
    RecentActivityCardComponent,
    StatsBreakdownCardComponent,
    LeaderboardCardComponent,
    ErrorStateComponent,
    EmptyStateComponent,
  ],
  templateUrl: './student-dashboard.component.html',
  styleUrl: './student-dashboard.component.css',
})
export class StudentDashboardComponent implements OnInit {
  studentAssignments: StudentAssignment[] = [];
  currentUserName: string = '';
  currentUserAvatar: string = 'assets/images/default-user.png';
  progression: any = null;

  /** Erreur de chargement de la progression globale. */
  readonly progressionErrored = signal(false);

  /** Erreur de chargement de la progression par matière. */
  readonly subjectsErrored = signal(false);

  private studentTestService = inject(StudentTestService);
  private studentService = inject(StudentService);
  private gamificationService = inject(GamificationService);
  private ageTierService = inject(AgeTierService);
  private calendarService = inject(StudentCalendarService);
  private studentFeatures = inject(StudentFeatureService);
  private labels = inject(LocalizedLabelService);

  /** Classement Top 5. */
  leaderboardEntries: LeaderboardEntry[] = [];
  leaderboardLoading = true;

  /** Résumé XP de l'élève connecté. */
  xpSummary: StudentXpSummary | null = null;

  readonly currentTier = this.ageTierService.currentTier;

  /** Séances de l'élève sur la quinzaine à venir. */
  readonly sessions = signal<AcademicAppointment[]>([]);
  readonly sessionsLoading = signal(true);
  readonly sessionsErrored = signal(false);

  /**
   * Le pack ouvre-t-il l'agenda ? Sans lui, le serveur refuse le calendrier
   * (SKILL2ERA-137) : le bloc des séances disparaît au lieu de lire une route
   * fermée — un 403 en lecture renverrait l'élève hors de son tableau de bord.
   */
  readonly showsSessions = signal(true);

  /** Le bouton « diagnostic » ne mène qu'à un diagnostic que le pack ouvre. */
  readonly showsDiagnostic = signal(false);

  private readonly today = new Date().toISOString().slice(0, 10);

  constructor(private authService: AuthService) {}

  async ngOnInit(): Promise<void> {
    await this.loadUserInfo();
    this.loadProgression();
    this.loadSubjectsProgression();
    void this.loadSessions();
    this.loadLeaderboard();
    this.loadXpSummary();
    this.applyTierFromStudentProfile();

    this.authService.studentAssignments$.subscribe({
      next: (assignment) => {
        this.studentAssignments = assignment;
      },
      error: (err) => console.error(err),
    });
  }

  /** Séances du jour et de la quinzaine — alimente « Programme d'aujourd'hui ». */
  private async loadSessions(): Promise<void> {
    await this.studentFeatures.load();
    this.showsSessions.set(this.studentFeatures.allows('agenda'));
    this.showsDiagnostic.set(this.studentFeatures.allows('diagnostic'));
    if (!this.showsSessions()) return;

    const to = new Date();
    to.setDate(to.getDate() + 14);

    this.sessionsLoading.set(true);
    this.sessionsErrored.set(false);

    this.calendarService.myCalendar(this.today, to.toISOString().slice(0, 10)).subscribe({
      next: (res) => {
        const list = [...(res?.data ?? [])].sort((a, b) =>
          sessionStart(a).localeCompare(sessionStart(b)),
        );
        this.sessions.set(list);
        this.sessionsLoading.set(false);
      },
      error: () => {
        // Une liste vide reste vide : aucune séance de démonstration.
        this.sessions.set([]);
        this.sessionsErrored.set(true);
        this.sessionsLoading.set(false);
      },
    });
  }

  reloadSessions(): void {
    void this.loadSessions();
  }

  /** Parcours de l'élève, affiché dans le bandeau d'identité. */
  readonly learningPathName = signal<string | null>(null);

  private async applyTierFromStudentProfile(): Promise<void> {
    try {
      const student = await this.authService.getStudent();
      if (student?.birthDate) {
        this.ageTierService.setTierFromBirthDate(student.birthDate);
      }
      // Le parcours détermine ce que l'élève voit — matières, tests, supports :
      // il mérite d'être affiché, pas seulement subi.
      this.learningPathName.set(student?.learningPath?.title ?? null);
    } catch (err) {
      console.warn('Could not derive age tier from birthDate:', err);
    }
  }

  get progressionScore(): number {
    return this.progression?.overallAverageScore != null
      ? Math.round(this.progression.overallAverageScore)
      : 0;
  }

  get testStats(): any[] {
    const p = this.progression;
    return [
      { id: 1, icon: 'fa-solid fa-list-check mt-2',  title: 'DASHBOARD.TOTAL_TESTS', points: p?.totalTestsTaken ?? '—' },
      { id: 2, icon: 'fa-solid fa-circle-check mt-2', title: 'DASHBOARD.PASSED',      points: p?.totalTestsPassed ?? '—' },
      { id: 3, icon: 'fa-solid fa-circle-xmark mt-2', title: 'DASHBOARD.FAILED',      points: p?.totalTestsFailed ?? '—' },
      { id: 4, icon: 'fa-solid fa-percent mt-2',      title: 'DASHBOARD.PASS_RATE',
        points: p?.passRate != null ? Math.round(p.passRate) + '%' : '—' },
    ];
  }

  get recentHistory(): RecentResult[] {
    return this.progression?.progressionHistory?.slice(0, 5) ?? [];
  }

  // ─── Primary tier ─────────────────────────────────────────────
  /** Prénom réel de l'élève. Vide tant qu'il n'est pas chargé — pas de prénom d'exemple. */
  get primFirstName(): string {
    return (this.currentUserName || '').split(' ')[0];
  }

  /** Prochaine séance à venir, pour l'encart d'accueil. */
  readonly nextSession = computed(() => {
    const now = Date.now();
    return this.sessions().find((s) => new Date(sessionStart(s)).getTime() >= now) ?? null;
  });

  get primXp(): PrimXpData {
    // Utilise les données XP réelles du backend quand elles sont disponibles
    if (this.xpSummary) {
      const xp = this.xpSummary;
      const progress = xp.xpToNextLevel > 0
        ? Math.round((xp.xpInCurrentLevel / (xp.xpInCurrentLevel + xp.xpToNextLevel)) * 100)
        : 100;
      return {
        stars: xp.totalXp,
        level: xp.level,
        progress,
        toNext: xp.xpToNextLevel,
      };
    }

    // Fallback : calcul côté frontend si le backend XP n'est pas encore chargé
    const stars = this.progression?.totalTestsPassed != null
      ? this.progression.totalTestsPassed * 20
      : 0;
    const score = this.progressionScore;
    const level = Math.max(1, Math.floor(stars / 100) + 1);
    const progress = score;
    const toNext = Math.max(0, 100 - score);
    return { stars, level, progress, toNext };
  }

  /**
   * Subjects displayed on the dashboard, alimentées uniquement par le backend.
   * Une liste vide reste vide : aucune donnée de démonstration.
   */
  dashboardSubjects: DashboardSubject[] = [];

  /** Backward-compat alias used by the primary template. */
  get primSubjects(): DashboardSubject[] { return this.dashboardSubjects; }

  private readonly subjectsPalette = [
    'var(--cred5)',
    'var(--cgreen5)',
    'var(--cblue5)',
    'var(--cpurple1)',
    'var(--cyellow4)',
    'var(--cpurple0)',
  ];

  /** Map the API DTO into the lightweight shape the UI expects. */
  private mapProgressionToSubject(
    p: StudentSubjectProgression,
    index: number,
  ): DashboardSubject {
    return {
      name: this.labels.pickPrefixed(p as unknown as Record<string, unknown>, 'subjectName'),
      // Pas d'icône : l'heuristique précédente testait des noms français
      // (« math », « franç », « scien ») alors que le catalogue est en arabe —
      // aucune matière ne correspondait, et `iconUrl` porte des noms Material
      // Symbols que la police FontAwesome du produit ne rend pas.
      color: this.subjectsPalette[index % this.subjectsPalette.length],
      progress: Math.max(0, Math.min(100, Math.round(p.progressionRate ?? 0))),
      // Le serveur rend les favorites en tête ; l'étoile dit *pourquoi* elles y
      // sont. Sans elle, l'ordre passe pour un hasard.
      isFavorite: p.isFavorite,
    };
  }

  /**
   * Resolve a FontAwesome class for a subject. Prefers `iconUrl` when it
   * already looks like a FA class; otherwise falls back to a heuristic based
   * on the (FR) subject name.
   */
  private pickSubjectIcon(name: string, iconUrl?: string | null): string {
    if (iconUrl && iconUrl.includes('fa-')) return iconUrl;
    const n = (name || '').toLowerCase();
    if (/math|calcul|alg|géom|geom/.test(n))     return 'fa-solid fa-calculator';
    if (/franç|french|lecture|écrit/.test(n))    return 'fa-solid fa-book-open';
    if (/hist|géo|geo|monde/.test(n))            return 'fa-solid fa-earth-europe';
    if (/scien|svt|phys|chim|bio/.test(n))       return 'fa-solid fa-flask';
    if (/art|dess|peint|plast/.test(n))          return 'fa-solid fa-palette';
    if (/musi/.test(n))                           return 'fa-solid fa-music';
    if (/sport|eps|gym/.test(n))                  return 'fa-solid fa-person-running';
    if (/info|tech|prog|code/.test(n))            return 'fa-solid fa-laptop-code';
    if (/angl|english/.test(n))                   return 'fa-solid fa-language';
    if (/arab|عرب/.test(n))                        return 'fa-solid fa-language';
    if (/philo/.test(n))                          return 'fa-solid fa-brain';
    if (/écon|gestion/.test(n))                  return 'fa-solid fa-chart-line';
    return 'fa-solid fa-book';
  }

  /** Rechargement manuel de la progression par matière (<app-error-state>). */
  reloadSubjects(): void {
    this.loadSubjectsProgression();
  }

  /** Pull the real subject set + progression from the backend. */
  private loadSubjectsProgression(): void {
    this.subjectsErrored.set(false);
    this.studentService.getMySubjectsProgression().subscribe({
      next: (res) => {
        const list = res?.data ?? [];
        this.dashboardSubjects = list.map((p, i) =>
          this.mapProgressionToSubject(p, i),
        );
      },
      error: () => {
        this.dashboardSubjects = [];
        this.subjectsErrored.set(true);
      },
    });
  }

  /**
   * Paliers de réussite. Chaque badge correspond à un nombre de tests
   * réellement réussis : le libellé dit ce qu'il mesure, et aucun badge
   * n'est verrouillé pour toujours.
   */
  get primBadges(): PrimBadge[] {
    const passed = this.progression?.totalTestsPassed ?? 0;
    const milestones = [
      { threshold: 1,  icon: 'fa-solid fa-seedling', color: 'var(--cgreen5)' },
      { threshold: 3,  icon: 'fa-solid fa-star',     color: 'var(--cyellow4)' },
      { threshold: 5,  icon: 'fa-solid fa-medal',    color: 'var(--cblue5)' },
      { threshold: 10, icon: 'fa-solid fa-trophy',   color: 'var(--cpurple1)' },
      { threshold: 20, icon: 'fa-solid fa-crown',    color: 'var(--cpurple0)' },
      { threshold: 50, icon: 'fa-solid fa-gem',      color: 'var(--cred5)' },
    ];

    return milestones.map((m) => ({
      icon: m.icon,
      i18nKey: 'DASHBOARD.PRIMARY.BADGE_MILESTONE',
      i18nParams: { count: m.threshold },
      color: m.color,
      locked: passed < m.threshold,
    }));
  }

  /** Rechargement manuel de la progression globale (<app-error-state>). */
  reloadProgression(): void {
    this.loadProgression();
  }

  private loadProgression(): void {
    this.progressionErrored.set(false);
    this.studentTestService.getStudentProgression().subscribe({
      next: (res) => {
        this.progression = res?.data ?? null;
      },
      error: () => {
        this.progression = null;
        this.progressionErrored.set(true);
      },
    });
  }

  private async loadUserInfo(): Promise<void> {
    try {
      this.currentUserName = await this.authService.getUserDisplayName();
      this.currentUserAvatar = await this.authService.getUserAvatar();
    } catch (error) {
      console.error('Error loading user info:', error);
      this.currentUserName = 'User';
      this.currentUserAvatar = 'assets/images/default-user.png';
    }
  }

  // ─── Gamification ──────────────────────────────────────────

  private loadLeaderboard(): void {
    this.leaderboardLoading = true;
    this.gamificationService.getLeaderboard(5).subscribe({
      next: (res) => {
        this.leaderboardEntries = res?.data ?? [];
        this.leaderboardLoading = false;
      },
      error: () => {
        this.leaderboardEntries = [];
        this.leaderboardLoading = false;
      },
    });
  }

  reloadLeaderboard(): void {
    this.loadLeaderboard();
  }

  private loadXpSummary(): void {
    this.gamificationService.getMyXpSummary().subscribe({
      next: (res) => {
        this.xpSummary = res?.data ?? null;
      },
      error: () => {
        this.xpSummary = null;
      },
    });
  }
}
